"use client";

import { ChevronDownIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import { useModal } from "@/components/ModalContext";
import { usePricing } from "@/contexts/PricingContext";
import { formatPrice } from "@/utils/utilities.mjs";
import LearnMoreModal from "./LearnMoreModal";

const SOLD_OUT_TEXT = " (Out of stock)";

function getVariantLabel(variant) {
  const sizeOption = variant.selectedOptions?.find(
    (option) => option.name.toLowerCase() === "size",
  );

  if (!sizeOption) return variant.title || variant.id;
  return sizeOption.value || variant.title || variant.id;
}

function getFullVariantLabel(variant) {
  const label = getVariantLabel(variant);
  return variant.availableForSale ? label : `${label}${SOLD_OUT_TEXT}`;
}

const CrossSellCard = ({
  productTitle,
  image,
  description,
  modalLabel,
  selectedVariant,
  isChecked,
  isSoldOut,
  toggleCrossSell,
  isUnlocked = false,
  variants = [],
  onVariantChange,
}) => {
  const { openModal } = useModal();
  const { variant: getVariantPrices } = usePricing();

  if (!selectedVariant) return null;

  const prices = getVariantPrices(selectedVariant);
  const sale = Number.isFinite(prices.sale)
    ? prices.sale
    : parseFloat(selectedVariant.price?.amount ?? 0);
  const full = Number.isFinite(prices.full) ? prices.full : sale;
  const showStrike = prices.hasPriceDifference && full > sale;

  const isDisabled = isSoldOut;

  const handleLearnMore = () => {
    openModal(
      <LearnMoreModal
        image={image}
        productTitle={productTitle}
        description={description}
      />,
      "max-w-3xl xl:max-w-5xl 2xl:max-w-6xl",
      "bg-warm-white",
      { ariaLabel: `Learn more about ${productTitle}` },
    );
  };

  return (
    <div
      data-area="cross-sell"
      className="addon-container grid gap-2 grid-flow-row grid-cols-[3fr_1fr]"
    >
      <div
        className={`col-span-2 border-2 border-white transition-opacity duration-150 ${isSoldOut ? "opacity-60" : ""}`}
      >
        <div className="relative h-fit">
          <label
            className={`grid grid-cols-[2rem_1fr_0.5fr] h-full bg-white gap-2 p-3 border-2 border-white ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
          >
            <input
              className="hidden sr-only peer"
              type="checkbox"
              autoComplete="off"
              value={selectedVariant.id}
              checked={isChecked}
              onChange={toggleCrossSell}
              disabled={isDisabled}
            />

            <span className="w-8 h-8 shrink-0 rounded-full border-2 border-sand bg-no-repeat bg-center self-center peer-checked:border-sea peer-checked:bg-white-checkmark peer-focus-visible:ring-2 peer-focus-visible:ring-sea peer-focus-visible:ring-offset-2" />

            <div className="self-center">
              <p className="font-worksans text-primary text-sm md:text-base leading-5">
                {productTitle}
              </p>

              <p className="text-xs md:text-sm">
                {sale > 0 && (
                  <strong className="font-worksans text-primary font-medium text-sm">
                    {formatPrice(sale)}
                  </strong>
                )}{" "}
                {showStrike && (
                  <s className="font-worksans text-primary line-through font-light text-sm">
                    {formatPrice(full)}
                  </s>
                )}
              </p>
            </div>

            {image?.url && (
              <div className="flex justify-end">
                <div className="w-[80px] h-[80px] relative shrink-0">
                  <Image
                    src={image.url}
                    alt=""
                    fill
                    className="object-cover"
                    sizes="160px"
                  />
                </div>
              </div>
            )}
          </label>
        </div>
      </div>

      <div
        className={`qa-addon-option relative bg-white w-full border-2 border-white text-sm leading-none flex items-center py-2 pl-3 pr-10 ${isUnlocked ? "cursor-pointer" : "opacity-70 cursor-not-allowed"}`}
      >
        {isUnlocked && (
          <select
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer appearance-none"
            value={selectedVariant.id}
            onChange={(e) => onVariantChange?.(e.target.value)}
            aria-label="Select product variant"
          >
            {variants.map((v) => (
              <option key={v.id} value={v.id}>
                {getFullVariantLabel(v)}
              </option>
            ))}
          </select>
        )}
        <span className="min-w-0 flex-1 text-primary">
          {getFullVariantLabel(selectedVariant)}
        </span>
        <ChevronDownIcon
          className="pointer-events-none absolute right-3 top-1/2 size-5 shrink-0 -translate-y-1/2 text-sea"
          aria-hidden
        />
      </div>

      <button
        type="button"
        onClick={handleLearnMore}
        aria-label={`Learn more about ${productTitle}`}
        aria-haspopup="dialog"
        className="min-w-max p-2 text-xs md:px-2 md:py-0 bg-white text-primary font-medium learn-more-button grid content-center cursor-pointer"
      >
        {modalLabel}
      </button>
    </div>
  );
};

export default CrossSellCard;
